import React, { useContext } from "react";
import classNames from "classnames";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { StateAwareNavLink } from "@components/routes/StateAwareNavLink";
import { AccountingReportContext } from "../AccountingReport.context";
import { useReportAmountDisplay } from "../useReportAmountDisplay";
import {
    isConsolidatedTotalColumn,
    isEliminationsColumn,
} from "../consolidatedColumns";
import { buildEliminationDetailUrl } from "../buildEliminationDetailUrl";
import { getDrillDownClassParams } from "../useGLDrillDownUrl";
import { ProfitLossDrillDownParams } from "./profitLossDrillDown.lib";
import styles from "./ProfitLossTable.module.scss";

interface Props {
    value: number;
    accountCodes?: number[];
    period: ReportPeriodWithLabel;
    isGroupEnd?: boolean;
    isTotal: boolean;
    isSummary: boolean;
    /** Present only on counterparty rows. `null` = unverified/empty counterparty. */
    counterpartyId?: string | null;
    /** Opens the GL drill-down drawer in place instead of navigating away. */
    onDrillDown: (params: ProfitLossDrillDownParams) => void;
}

/**
 * ViewTable-compatible P&L value cell.
 * Renders a <div> (not <td>) so it works correctly inside ViewTable's CSS-grid
 * rows.  Applies isTotal / isSummary row-level styling at the cell level since
 * ViewTable does not expose a row-class API.
 *
 * Counterparty rows drill to GL pre-filtered to the row's counterparty so the
 * whole P&L report shares one drill-down destination.
 */
export const ProfitLossViewValueCell: React.FC<Props> = React.memo(
    ({
        value,
        accountCodes,
        period,
        isGroupEnd,
        isTotal,
        isSummary,
        counterpartyId,
        onDrillDown,
    }) => {
        const { filters } = useContext(AccountingReportContext);
        const { text: displayValue, negativeClassName } =
            useReportAmountDisplay(value, {
                comparisonDiff: period.comparisonDiff,
                ratioType: period.ratioType,
            });
        const isEliminations = isEliminationsColumn(period);
        const eliminationAccountCode =
            accountCodes?.length === 1 ? accountCodes[0] : undefined;
        const eliminationDetailUrl = buildEliminationDetailUrl({
            startDate: period.startDate ?? filters.startDate,
            endDate: period.endDate ?? filters.endDate,
            workspaceId: filters.workspaceId,
            consolidationGroupId: filters.consolidationGroupId,
            accountCode: eliminationAccountCode,
        });

        const isDrillDownInteractive =
            Boolean(value) &&
            !period.comparisonDiff &&
            !period.ratioType &&
            !isEliminations &&
            !isConsolidatedTotalColumn(period);
        const isEliminationInteractive =
            Boolean(value) &&
            !period.comparisonDiff &&
            !period.ratioType &&
            isEliminations &&
            Boolean(filters.consolidationGroupId) &&
            Boolean(filters.workspaceId);

        const counterpartyFilters =
            counterpartyId !== undefined
                ? {
                      counterpartiesIds: counterpartyId
                          ? [counterpartyId]
                          : undefined,
                      withEmptyOrUnverifiedCounterparty:
                          counterpartyId === null ? true : undefined,
                  }
                : {
                      counterpartiesIds: filters.counterpartiesIds,
                      withEmptyOrUnverifiedCounterparty:
                          filters.withEmptyOrUnverifiedCounterparty,
                      hasCounterparty: filters.hasCounterparty,
                  };

        const drillDownParams: ProfitLossDrillDownParams = {
            accountCodes,
            startDate: period.startDate ?? filters.startDate,
            endDate: period.endDate ?? filters.endDate,
            entityId: period.entityId ?? filters.entityId,
            ledgerId: filters.ledgerId,
            ...getDrillDownClassParams(period, filters),
            ...counterpartyFilters,
            accountIds: filters.accountIds,
            subaccountIds: filters.subaccountIds,
        };

        let cellContent: React.ReactNode = displayValue;
        if (isDrillDownInteractive) {
            cellContent = (
                <button
                    type="button"
                    className={classNames(
                        "data-table__drill-down",
                        styles.drillDownButton,
                    )}
                    onClick={() => onDrillDown(drillDownParams)}
                >
                    {displayValue}
                </button>
            );
        } else if (isEliminationInteractive) {
            cellContent = (
                <StateAwareNavLink
                    to={eliminationDetailUrl}
                    className="data-table__drill-down"
                >
                    {displayValue}
                </StateAwareNavLink>
            );
        }

        return (
            <ViewTableCell
                className={classNames(styles.numericCell, negativeClassName, {
                    [styles.totalCell]: isTotal,
                    [styles.summaryCell]: isSummary,
                    [styles.groupEndCell]: isGroupEnd,
                })}
            >
                {cellContent}
            </ViewTableCell>
        );
    },
);
